/**
 * MCAT score sums and AAMC percentile ranks.
 *
 * Source: AAMC, "Summary of MCAT Total and Section Scores: Percentile Ranks in Effect
 * May 1, 2026 - April 30, 2027" (N = 305,494), https://www.aamc.org/media/89826/download.
 * Retrieved 2026-10-16 (text extracted from the PDF and checked against its rendered page).
 * AAMC: the percentile rank is "the percentage of scores equal to or less than each score
 * point", based on all MCAT results from the 2023, 2024 and 2025 testing years combined, and
 * the ranks are updated each May 1. Total: mean 500.6, standard deviation 11.2. Section means
 * (standard deviations): C/P 124.9 (3.2), CARS 124.6 (2.9), B/B 125.1 (3.2), P/S 125.9 (3.3).
 *
 * The total score is the sum of the four section scores, each 118-132, so it runs from 472
 * to 528.
 */

export const MCAT_RANKS_IN_EFFECT = "May 1, 2026 to April 30, 2027";
export const MCAT_TESTING_YEARS = "2023, 2024 and 2025";
export const MCAT_SCORES_N = 305_494;
export const MCAT_SECTION_MIN = 118;
export const MCAT_SECTION_MAX = 132;
export const MCAT_TOTAL_MIN = 472;
export const MCAT_TOTAL_MAX = 528;

export type McatSection = "cp" | "cars" | "bb" | "ps";
export const MCAT_SECTIONS: readonly McatSection[] = ["cp", "cars", "bb", "ps"];

/** Percentile ranks for 118 up to 132. */
const SECTION_RANKS: Record<McatSection, readonly number[]> = {
  cp: [2, 4, 9, 16, 25, 35, 46, 56, 67, 77, 85, 91, 96, 99, 100],
  cars: [1, 3, 8, 15, 25, 37, 50, 62, 74, 84, 90, 95, 98, 99, 100],
  bb: [1, 4, 8, 15, 23, 32, 43, 53, 64, 75, 84, 90, 96, 98, 100],
  ps: [1, 3, 6, 10, 17, 25, 34, 44, 54, 65, 76, 85, 92, 97, 100],
};

/** Total-score percentile ranks, 472 up to 528; "<1" in AAMC's table is stored as 0. */
const TOTAL_RANKS: readonly number[] = [
  0, 0, 0, 1, 1, 2, 2, 3, 4, 5, 6, 8, 9, 11, 12, 14, 16, 18, 20, 23, 25, 28, 31,
  33, 36, 39, 42, 45, 48, 52, 55, 58, 61, 64, 67, 71, 74, 76, 79, 82, 84, 86,
  89, 91, 92, 94, 95, 96, 97, 98, 99, 99, 99, 100, 100, 100, 100,
];

/** Totals AAMC prints as "<1". */
export const MCAT_LESS_THAN_ONE_MAX = 474;

export function isMcatSectionScore(value: number | null): value is number {
  return (
    value !== null &&
    Number.isInteger(value) &&
    value >= MCAT_SECTION_MIN &&
    value <= MCAT_SECTION_MAX
  );
}

export function isMcatTotal(value: number | null): value is number {
  return (
    value !== null &&
    Number.isInteger(value) &&
    value >= MCAT_TOTAL_MIN &&
    value <= MCAT_TOTAL_MAX
  );
}

/** The total score for four valid section scores. */
export function mcatTotal(sections: readonly number[]): number {
  return sections.reduce((sum, score) => sum + score, 0);
}

export interface McatRank {
  /** Percentage of scores equal to or less than the score. */
  rank: number;
  /** True where AAMC prints "<1" rather than a number. */
  lessThanOne: boolean;
}

export function mcatSectionRank(
  section: McatSection,
  score: number
): McatRank | null {
  if (!isMcatSectionScore(score)) {
    return null;
  }
  const rank = SECTION_RANKS[section][score - MCAT_SECTION_MIN];
  return rank === undefined ? null : { rank, lessThanOne: false };
}

export function mcatTotalRank(total: number): McatRank | null {
  if (!isMcatTotal(total)) {
    return null;
  }
  const rank = TOTAL_RANKS[total - MCAT_TOTAL_MIN];
  return rank === undefined
    ? null
    : { rank, lessThanOne: total <= MCAT_LESS_THAN_ONE_MAX };
}

export interface McatSectionRow {
  score: number;
  ranks: Record<McatSection, number>;
}

export const MCAT_SECTION_ROWS: readonly McatSectionRow[] = Array.from(
  { length: MCAT_SECTION_MAX - MCAT_SECTION_MIN + 1 },
  (_, index) => {
    const score = MCAT_SECTION_MAX - index;
    return {
      score,
      ranks: {
        cp: SECTION_RANKS.cp[score - MCAT_SECTION_MIN] ?? 0,
        cars: SECTION_RANKS.cars[score - MCAT_SECTION_MIN] ?? 0,
        bb: SECTION_RANKS.bb[score - MCAT_SECTION_MIN] ?? 0,
        ps: SECTION_RANKS.ps[score - MCAT_SECTION_MIN] ?? 0,
      },
    };
  }
);

export interface McatTotalRow {
  total: number;
  rank: McatRank;
}

/** Every total from 528 down to 472. */
export const MCAT_TOTAL_ROWS: readonly McatTotalRow[] = Array.from(
  { length: MCAT_TOTAL_MAX - MCAT_TOTAL_MIN + 1 },
  (_, index) => {
    const total = MCAT_TOTAL_MAX - index;
    return {
      total,
      rank: mcatTotalRank(total) ?? { rank: 0, lessThanOne: false },
    };
  }
);

/** The lowest total whose percentile rank is at least `target`, or null. */
export function mcatLowestTotalForRank(target: number): number | null {
  const row = [...MCAT_TOTAL_ROWS]
    .reverse()
    .find((entry) => !entry.rank.lessThanOne && entry.rank.rank >= target);
  return row ? row.total : null;
}
